"use client";

import { c as _c } from "react/compiler-runtime";
import ProfileAvatar, { ProfileBadgeSize } from "@/components/common/profile/ProfileAvatar";
import { formatFullDateTime, getNextMintStart } from "@/components/meme-calendar/meme-calendar.helpers";
import DropListItemContentMedia from "@/components/drops/view/item/content/media/DropListItemContentMedia";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { formatNumberWithCommas } from "@/helpers/Helpers";
import { getScaledImageUri, ImageScale } from "@/helpers/image.helpers";
import { getDropPreviewImageUrl } from "@/helpers/waves/drop.helpers";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import Image from "next/image";
import Link from "next/link";
import NowMintingStatsItem from "./NowMintingStatsItem";
interface LatestDropNextMintSectionProps {
  readonly drop: ApiDrop;
}
const formatDropTimestamp = (timestamp: number): string | null => {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  const dateLabel = new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric"
  }).format(date);
  const timeLabel = new Intl.DateTimeFormat(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false
  }).format(date);
  return `${dateLabel} · ${timeLabel}`;
};
export default function LatestDropNextMintSection(t0) {
  const $ = _c(52);
  const {
    drop
  } = t0;
  const {
    hasTouchScreen
  } = useDeviceInfo();
  const media = drop.parts[0]?.media[0];
  const isHtml = media?.mime_type === "text/html";
  let t1;
  if ($[0] !== drop.metadata || $[1] !== hasTouchScreen || $[2] !== isHtml) {
    t1 = hasTouchScreen && isHtml ? getDropPreviewImageUrl(drop.metadata) ?? undefined : undefined;
    $[0] = drop.metadata;
    $[1] = hasTouchScreen;
    $[2] = isHtml;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const htmlPreviewImageUrl = t1;
  const title = drop.title ?? drop.metadata.find(_temp)?.data_value ?? "Untitled";
  const author = drop.author;
  const authorHandle = author.handle ?? author.primary_address;
  const authorName = author.handle ?? "Anonymous";
  let t2;
  if ($[4] !== drop.created_at) {
    t2 = formatDropTimestamp(drop.created_at);
    $[4] = drop.created_at;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const submittedAt = t2;
  const description = drop.metadata.find(_temp2)?.data_value ?? null;
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    const nextMintStart = getNextMintStart();
    t3 = formatFullDateTime(nextMintStart, "local");
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const nextMintLabel = t3;
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <span className="tw-mb-3 tw-block tw-text-xl tw-font-semibold tw-tracking-tight tw-text-iron-100 md:tw-mb-4 md:tw-text-2xl">Next Drop</span>;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== hasTouchScreen || $[9] !== htmlPreviewImageUrl || $[10] !== media) {
    t5 = <div className="tw-p-0 lg:tw-col-span-6 xl:tw-col-span-5"><div className="tw-relative tw-flex tw-h-[clamp(360px,65vw,640px)] tw-w-full tw-items-center tw-justify-center tw-overflow-hidden tw-bg-black/50"><div className="tw-[&>div]:tw-mx-0 tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center">{media ? <DropListItemContentMedia media_mime_type={media.mime_type} media_url={media.url} imageObjectPosition="center" imageScale={ImageScale.AUTOx600} disableAutoPlay={hasTouchScreen} disableModal={hasTouchScreen} htmlIframeContainerClassName="tw-w-full" htmlPreviewImageUrl={htmlPreviewImageUrl} /> : <div className="tw-flex tw-size-full tw-items-center tw-justify-center tw-bg-black/40"><span className="tw-text-sm tw-text-white/40">No image</span></div>}</div></div></div>;
    $[8] = hasTouchScreen;
    $[9] = htmlPreviewImageUrl;
    $[10] = media;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2"><span className="tw-size-1.5 tw-rounded-full tw-bg-emerald-500" /><span className="tw-text-[11px] tw-font-semibold tw-uppercase tw-leading-5 tw-tracking-wide tw-text-emerald-400">NEXT MINT</span></div>;
    t7 = <span className="tw-mt-1 tw-font-mono tw-text-xs tw-text-white/50">{nextMintLabel}</span>;
    $[12] = t6;
    $[13] = t7;
  } else {
    t6 = $[12];
    t7 = $[13];
  }
  const t8 = `/waves?wave=${drop.wave.id}&drop=${drop.id}`;
  let t9;
  if ($[14] !== t8 || $[15] !== title) {
    t9 = <Link href={t8} className="tw-mt-3 tw-text-xl tw-font-semibold tw-leading-tight tw-text-iron-50 tw-no-underline tw-transition-colors tw-duration-300 desktop-hover:hover:tw-text-iron-200 sm:tw-text-2xl md:tw-text-3xl">{title}</Link>;
    $[14] = t8;
    $[15] = title;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== description) {
    t10 = description && <p className="tw-mt-3 tw-line-clamp-2 tw-text-sm tw-leading-relaxed tw-text-iron-300">{description}</p>;
    $[17] = description;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== author.pfp || $[20] !== authorHandle || $[21] !== authorName) {
    t11 = authorHandle ? <Link href={`/${authorHandle}`} className="tw-mt-3 tw-flex tw-min-w-0 tw-items-center tw-gap-2 tw-no-underline"><ProfileAvatar pfpUrl={author.pfp} alt={authorHandle || authorName} size={ProfileBadgeSize.SMALL} /><span className="tw-min-w-0 tw-truncate tw-text-sm tw-text-iron-200 desktop-hover:hover:tw-text-iron-100">{authorName}</span></Link> : <div className="tw-mt-3 tw-flex tw-min-w-0 tw-items-center tw-gap-2"><ProfileAvatar pfpUrl={author.pfp} alt={authorName} size={ProfileBadgeSize.SMALL} /><span className="tw-min-w-0 tw-truncate tw-text-sm tw-text-iron-200">{authorName}</span></div>;
    $[19] = author.pfp;
    $[20] = authorHandle;
    $[21] = authorName;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t10 || $[24] !== t11 || $[25] !== t9) {
    t12 = <div className="tw-flex tw-flex-col">{t6}{t7}{t9}{t10}{t11}</div>;
    $[23] = t10;
    $[24] = t11;
    $[25] = t9;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  const t13 = `/waves/${drop.wave.id}`;
  let t14;
  if ($[27] !== drop.wave.name || $[28] !== drop.wave.picture) {
    t14 = drop.wave.picture && <Image src={getScaledImageUri(drop.wave.picture, ImageScale.W_AUTO_H_50)} alt={drop.wave.name} width={20} height={20} className="tw-size-5 tw-shrink-0 tw-rounded-full tw-object-cover tw-ring-1 tw-ring-white/10" />;
    $[27] = drop.wave.name;
    $[28] = drop.wave.picture;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== drop.wave.name) {
    t15 = <span className="tw-min-w-0 tw-break-words">{drop.wave.name}</span>;
    $[30] = drop.wave.name;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== t13 || $[33] !== t14 || $[34] !== t15) {
    t16 = <div className="tw-col-span-2"><NowMintingStatsItem label="Wave" allowWrap={true} value={<Link href={t13} className="tw-inline-flex tw-items-center tw-gap-2 tw-text-iron-100 tw-no-underline desktop-hover:hover:tw-text-iron-200">{t14}{t15}</Link>} /></div>;
    $[32] = t13;
    $[33] = t14;
    $[34] = t15;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  const t17 = submittedAt ?? "\u2014";
  let t18;
  if ($[36] !== t17) {
    t18 = <NowMintingStatsItem label="Submitted" value={t17} />;
    $[36] = t17;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== drop.rating) {
    t19 = formatNumberWithCommas(drop.rating);
    $[38] = drop.rating;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  const t20 = `${t19} TDH`;
  let t21;
  if ($[40] !== t20) {
    t21 = <NowMintingStatsItem label="Rating" value={t20} />;
    $[40] = t20;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  let t22;
  if ($[42] !== t16 || $[43] !== t18 || $[44] !== t21) {
    t22 = <div className="tw-grid tw-grid-cols-2 tw-gap-x-6 tw-gap-y-4 tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-white/5 tw-pt-4">{t16}{t18}{t21}</div>;
    $[42] = t16;
    $[43] = t18;
    $[44] = t21;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  let t23;
  if ($[46] !== t12 || $[47] !== t22) {
    t23 = <div className="tw-p-5 md:tw-p-6 lg:tw-col-span-6 xl:tw-col-span-4"><div className="tw-flex tw-flex-col tw-gap-5">{t12}{t22}</div></div>;
    $[46] = t12;
    $[47] = t22;
    $[48] = t23;
  } else {
    t23 = $[48];
  }
  let t24;
  if ($[49] !== t23 || $[50] !== t5) {
    t24 = <section className="tw-relative tw-z-50 tw-px-4 tw-pb-4 tw-pt-6 md:tw-px-6 md:tw-pb-8 md:tw-pt-10 lg:tw-px-8">{t4}<div className="tw-relative tw-overflow-hidden tw-rounded-2xl tw-border tw-border-solid tw-border-white/[0.03] tw-bg-iron-950"><div className="tw-grid tw-grid-cols-1 tw-items-center tw-gap-x-6 tw-gap-y-6 lg:tw-grid-cols-12 xl:tw-grid-cols-9">{t5}{t23}</div></div></section>;
    $[49] = t23;
    $[50] = t5;
    $[51] = t24;
  } else {
    t24 = $[51];
  }
  return t24;
}
function _temp2(m_0) {
  return m_0.data_key === "description";
}
function _temp(m) {
  return m.data_key === "title";
}